import { FC, useEffect, useMemo } from "react";
import { observer } from "mobx-react";
import { Typography, useTheme } from "@mui/material";

import CoreVirtualizedTable from "../ui/core/CoreVirtualizedTable";
import { useTicketStore } from "../../hooks/useTicketStore";
import { CONTACT_SUPPORT_PURPOSE_TYPES } from "../../config/constants";
import type {
  ITicket,
  TicketColumnFilters,
  TicketsTableColumnId,
} from "../../types/tickets";
import { TICKETS_TABLE_COLUMN_OPTIONS } from "../../types/tickets";
import {
  ColumnFilterType,
  ColumnFiltersConfig,
  FilterOperator,
  SimpleColumnDef,
  SortingState,
} from "../../types/table";
import { formatInstant } from "../helpers/dataHelper";
import TicketStatusChip from "./TicketStatusChip";
import TicketPriorityChip from "./TicketPriorityChip";
import { formatRelativeUpdate, TICKET_STATUS_LABELS } from "./ticketDisplay";

// Filtering is server-side: applying/clearing a column filter round-trips to
// the tickets list endpoint (see TicketStore.setColumnFilter), the same way
// ClaimsTable's column filters do. No in-memory filtering here.
const FILTERABLE_COLUMNS = [
  "number",
  "title",
  "status",
  "priority",
  "created_at",
  "updated_at",
  "replies",
] as const;

const COLUMN_FILTER_TYPES: Record<string, ColumnFilterType> = {
  number: "text",
  title: "text",
  created_at: "dateRange",
  updated_at: "dateRange",
  replies: "priceRange",
};

// Subject filter suggests the same Purpose options as the New Request panel's
// Purpose dropdown, since a ticket's subject is generally purpose-derived.
const SUBJECT_FILTER_OPTIONS = Object.values(CONTACT_SUPPORT_PURPOSE_TYPES);

const getColumnFilterValues = (
  filters: TicketColumnFilters | null,
  columnId: string,
): string[] => {
  const raw = filters?.[columnId];
  if (!raw) return [];
  return Array.isArray(raw)
    ? raw.filter(Boolean)
    : (raw.values ?? []).map(String);
};

const getColumnFilterOperator = (
  filters: TicketColumnFilters | null,
  columnId: string,
): FilterOperator => {
  const raw = filters?.[columnId];
  if (!raw || Array.isArray(raw)) return "and";
  return raw.operator;
};

interface TicketsTableProps {
  onSelectTicket: (ticketId: number) => void;
  selectedTicketId?: number | null;
}

export const TicketsTable: FC<TicketsTableProps> = observer(
  ({ onSelectTicket, selectedTicketId }) => {
    const theme = useTheme();
    const {
      tickets,
      isLoading,
      isLoadingMore,
      hasMoreTickets,
      columnFilters,
      orderBy,
      loadTickets,
      loadMoreTickets,
      setColumnFilter,
      clearColumnFilter,
      setOrderBy,
      isColumnVisible,
      isColumnRequired,
      toggleRequiredColumn,
      columnSettings,
    } = useTicketStore();

    useEffect(() => {
      loadTickets();
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    // Convert store orderBy to SortingState for the table
    const sorting: SortingState = useMemo(() => {
      if (!orderBy) return [];
      return [{ id: orderBy.field, desc: orderBy.direction === "desc" }];
    }, [orderBy]);

    const handleSortingChange = (newSorting: SortingState) => {
      if (newSorting.length === 0) {
        setOrderBy(null);
      } else {
        const { id, desc } = newSorting[0];
        setOrderBy({ field: id, direction: desc ? "desc" : "asc" });
      }
    };

    const columnFiltersFromStore = useMemo(() => {
      const filters: Record<
        string,
        { operator: FilterOperator; values: string[] }
      > = {};
      FILTERABLE_COLUMNS.forEach((columnId) => {
        filters[columnId] = {
          operator: getColumnFilterOperator(columnFilters, columnId),
          values: getColumnFilterValues(columnFilters, columnId),
        };
      });
      return filters;
    }, [columnFilters]);

    const numberFilterOptions = useMemo(
      () =>
        Array.from(new Set(tickets.map((ticket) => ticket.number))).sort(
          (a, b) => Number(b) - Number(a),
        ),
      [tickets],
    );

    const priorityFilterOptions = useMemo(() => {
      const byLabel = new Map<string, number>();
      tickets.forEach((ticket) => {
        if (ticket.priority) byLabel.set(ticket.priority, ticket.priority_id);
      });
      return Array.from(byLabel.entries())
        .sort(([, a], [, b]) => a - b)
        .map(([label]) => label);
    }, [tickets]);

    const columnFiltersConfig: ColumnFiltersConfig = useMemo(
      () => ({
        filters: columnFiltersFromStore,
        getOptions: (columnId) => {
          if (columnId === "status") return TICKET_STATUS_LABELS;
          if (columnId === "number") return numberFilterOptions;
          if (columnId === "priority") return priorityFilterOptions;
          // if (columnId === "title") return SUBJECT_FILTER_OPTIONS;
          return [];
        },
        onApply: (columnId, values, operator = "and") =>
          setColumnFilter(columnId, values, operator),
        onClear: (columnId) => clearColumnFilter(columnId),
        filterableColumns: [...FILTERABLE_COLUMNS],
        columnFilterTypes: COLUMN_FILTER_TYPES,
        isColumnPinned: isColumnRequired,
        onTogglePin: (columnId) =>
          toggleRequiredColumn(columnId as TicketsTableColumnId),
        canPinColumn: (columnId) =>
          !TICKETS_TABLE_COLUMN_OPTIONS.find((col) => col.id === columnId)
            ?.mandatory,
      }),
      [
        columnFiltersFromStore,
        numberFilterOptions,
        priorityFilterOptions,
        setColumnFilter,
        clearColumnFilter,
        isColumnRequired,
        toggleRequiredColumn,
      ],
    );

    const columns: SimpleColumnDef<ITicket>[] = useMemo(
      () => [
        {
          id: "number",
          header: "Ticket",
          accessorKey: "number",
          width: 110,
          minWidth: 100,
          headerAlign: "center",
          cell: (value) => (
            <Typography
              sx={{ fontWeight: 600, color: "primary.main", textAlign: "center" }}
            >{value}</Typography>
          ),
        },
        {
          id: "title",
          header: "Subject",
          accessorKey: "title",
          width: 320,
          minWidth: 180,
          cell: (value) => (
            <Typography noWrap title={value || ""}>
              {value || "-"}
            </Typography>
          ),
        },
        {
          id: "status",
          header: "Status",
          accessorKey: "state",
          width: 170,
          minWidth: 150,
          cell: (value) => (value ? <TicketStatusChip status={value} /> : "-"),
        },
        {
          id: "priority",
          header: "Priority",
          accessorKey: "priority",
          width: 120,
          minWidth: 100,
          cell: (value, row) => (
            <TicketPriorityChip priority={value} priorityId={row.priority_id} />
          ),
        },
        {
          id: "created_at",
          header: "Created",
          accessorKey: "created_at",
          width: 130,
          minWidth: 120,
          cell: (value) => (
            <Typography>{formatInstant(value, { dateOnly: true })}</Typography>
          ),
        },
        {
          id: "updated_at",
          header: "Last update",
          accessorKey: "updated_at",
          width: 130,
          minWidth: 110,
          cell: (value) => (
            <Typography>{formatRelativeUpdate(value)}</Typography>
          ),
        },
        // { Kepp for later use
        //   id: "replies",
        //   header: "Replies",
        //   accessorFn: (row) => row.reply_count ?? 0,
        //   width: 100,
        //   minWidth: 90,
        //   cell: (value, row) => (
        //     <Box sx={{ display: "flex", alignItems: "center", gap: 0.75 }}>
        //       <Typography>{value}</Typography>
        //       {row.has_unread_reply ? (
        //         <Box
        //           component="span"
        //           sx={{
        //             width: 6,
        //             height: 6,
        //             borderRadius: "50%",
        //             backgroundColor: "error.main",
        //           }}
        //         />
        //       ) : null}
        //     </Box>
        //   ),
        // },
      ],
      [],
    );

    // Filter columns based on visibility settings, pinned (required) columns first
    const visibleColumns = useMemo(() => {
      const visible = columns.filter((col) => isColumnVisible(col.id));
      const pinned = visible.filter((col) => isColumnRequired(col.id));
      const unpinned = visible.filter((col) => !isColumnRequired(col.id));
      return [...pinned, ...unpinned];
    }, [columns, isColumnVisible, isColumnRequired, columnSettings]);

    const handleRowClick = (ticket: ITicket) => {
      onSelectTicket(ticket.id);
    };

    return (
      <div
        style={{
          overflowX: "auto",
          background: "white",
          marginTop: "16px",
          borderRadius: "8px",
          border: "1px solid",
          borderColor: theme.palette.divider,
        }}
      >
        <CoreVirtualizedTable<ITicket>
          isLoading={isLoading}
          data={tickets}
          columns={visibleColumns}
          sorting={sorting}
          onSortingChange={handleSortingChange}
          height="calc(100vh - 220px)"
          minWidth={500}
          onRowClick={handleRowClick}
          getRowId={(row) => String(row.id)}
          selectedRowId={
            selectedTicketId != null ? String(selectedTicketId) : null
          }
          emptyMessage="No tickets found"
          onLoadMore={loadMoreTickets}
          hasMore={hasMoreTickets}
          isLoadingMore={isLoadingMore}
          columnFilters={columnFiltersConfig}
          disableLastOfTypeStyles
          disableFirstOfTypeStyles
          isResizable
          pinColumns
        />
      </div>
    );
  },
);

export default TicketsTable;
